import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

export interface DataTableColumn<T> {
  key: string;
  header: string;
  render: (row: T, index: number) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  isLoading?: boolean;
  loadingRows?: number;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
}

export function DataTable<T>({
  columns,
  data,
  isLoading,
  loadingRows = 5,
  emptyMessage,
  onRowClick,
}: DataTableProps<T>) {
  const { t } = useTranslation("producerCommon");
  const resolvedEmptyMessage = emptyMessage ?? t("dataTable.emptyMessage");
  const actionsColumn = columns.find((col) => col.key === "actions");
  const otherColumns = columns.filter((col) => col.key !== "actions" && col.key !== columns[0].key);

  return (
    <div className="space-y-3">
      {/* Desktop view */}
      <div className="hidden md:block rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key} className={col.className}>
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: loadingRows }).map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : data.length === 0
              ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center text-sm text-muted-foreground py-8">
                    {resolvedEmptyMessage}
                  </TableCell>
                </TableRow>
              )
              : data.map((row, i) => (
                  <TableRow
                    key={i}
                    className={onRowClick ? "cursor-pointer hover:bg-muted/50" : ""}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.key} className={col.className}>
                        {col.render(row, i)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile view */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border p-4 space-y-3 bg-card">
              <Skeleton className="h-5 w-2/3" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </div>
          ))
        ) : data.length === 0 ? (
          <div className="rounded-lg border border-border text-center text-sm text-muted-foreground py-8 bg-card">
            {resolvedEmptyMessage}
          </div>
        ) : (
          data.map((row, i) => (
            <div
              key={i}
              className={`rounded-xl border border-border p-4 bg-card shadow-sm hover:border-border transition-all flex flex-col gap-3 ${
                onRowClick ? "cursor-pointer" : ""
              }`}
              onClick={() => onRowClick?.(row)}
            >
              {/* Header inside mobile card */}
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  {columns[0].render(row, i)}
                </div>
                {actionsColumn && (
                  <div className="flex items-center gap-1 shrink-0 -mt-1" onClick={(e) => e.stopPropagation()}>
                    {actionsColumn.render(row, i)}
                  </div>
                )}
              </div>

              {/* Grid of properties */}
              {otherColumns.length > 0 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-2 border-t border-border/50">
                  {otherColumns.map((col) => (
                    <div key={col.key} className="space-y-0.5 min-w-0">
                      <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider block">
                        {col.header}
                      </span>
                      <div className="text-sm">
                        {col.render(row, i)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
